<template>
  <validation-observer ref="obs" v-slot="{ errors, invalid }" slim>
    <v-card>
      <v-toolbar color="titleBar" dark flat dense>
        <v-toolbar-title>{{ $t("create new customer") }}</v-toolbar-title>
        <v-spacer />
      </v-toolbar>
      <v-card-text>
        <v-card outlined class="mt-2">
          <v-card-text>
            <address-names
              v-model="address"
              company-name="company_name"
              company-max-length="100"
              first-name-max-length="30"
              last-name-max-length="30"
              :name-prefix="'address.'"
            />
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-2">
          <v-card-text>
            <address-postal
              v-model="address"
              additional-max-length="255"
              zip-name="zip_code"
              zip-max-length="10"
              city-max-length="100"
              street-max-length="100"
              street-rules="required"
              street-mumber-max-length="10"
              street-number-rules="required"
              :name-prefix="'address.'"
            />
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-2">
          <v-card-text>
            <address-contacts
              v-model="address"
              phone-max-length="16"
              :phone-rules="address.mobile ? '' : 'required' "
              mobile-max-length="16"
              :mobile-rules="address.phone ? '' : 'required'"
              fax-max-length="16"
              :name-prefix="'address.'"
              email-multiple
              email-max-length="100"
              email-required
            />
          </v-card-text>
        </v-card>

        <v-card outlined class="mt-2">
          <v-card-text>
            <validation-provider
                vid="billing_type"
                :name="$t('billing type')"
                v-slot="{ errors, valid, classes }"
                rules="required"
              >
                <v-select
                  name="billing_type"
                  v-model="billingTypeValue"
                  :items="billingTypes"
                  item-text="title_de"
                  item-value="id"
                  item-disabled="disabled"
                  :label="$t('billing type')"
                  :error-messages="errors"
                  :class="classes"
                  persistent-hint
                  menu-props="auto"
                >
                  <template v-slot:selection="{ item }">
                    {{ getBillingTypeTitle(item) }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ getBillingTypeTitle(item) }} {{ getBillingTypeNote(item) }}
                  </template>
                </v-select>
              </validation-provider>
              <validation-provider
                vid="billing_period"
                :name="$t('billing period')"
                v-slot="{ errors, valid, classes }"
                rules="required"
              >
                <v-select
                  name="billing_period"
                  v-model="billingPeriodValue"
                  :items="billingPeriodes"
                  item-text="title"
                  item-value="id"
                  :label="$t('billing period')"
                  :error-messages="errors"
                  :disabled="
                    $store.state.session.partnerType === 'WHO'
                  "
                  :class="classes"
                  persistent-hint
                  menu-props="auto"
                >
                 <template v-slot:selection="{ item }">
                    {{ $t(item.title) }}
                  </template>
                  <template v-slot:item="{ item }">
                    {{ $t(item.title) }}
                  </template>
                </v-select>
              </validation-provider>
            </v-card-text>  
          </v-card>
      </v-card-text>
      <v-card-actions>
        <v-btn
          :disabled="loading"
          @click="
            $emit('canceled');
            address = { ...emptyAddress };
          "
          text
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :disabled="loading || invalid"
          :loading="loading"
          @click="createCustomer"
          text
        >
          {{ $t("create") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </validation-observer>
</template>

<script>
import AddressNames from "@/components/basics/AddressNames";
import AddressPostal from "@/components/basics/AddressPostal";
import AddressContacts from "@/components/basics/AddressContacts";
import showErrors from "@/utils/mixins/showErrors";

var emptyAddress = {
  company_name: null,
  first_name: null,
  last_name: null,
  salutation: null,
  additional: null,
  street: null,
  street_number: null,
  zip_code: null,
  city: null,
  country: "CH",
  phone: null,
  mobile: null,
  fax: null,
  email: null,
};

export default {
  name: "CreateClientCustomer",
  mixins: [showErrors],
  components: {
    AddressNames,
    AddressPostal,
    AddressContacts,
  },
  data: () => ({
    loading: false,
    emptyAddress: emptyAddress,
    address: { ...emptyAddress },
    billingPeriodValue: "MIV",
    billingPeriodes: [
        { id: "MIV", title:"monthly in advance" },
        { id: "QIV", title: "quarterly in advance" },
        { id: "HIV", title: "every six months in advance" },
        { id: "JIV", title: "annually in advance"},
      ],
    billingTypeValue: 4,
  }),
  computed: {
    isWho() {
      return this.$store.state.partnerType === "WHO";
    },
    billingTypes() {
      return this.$config.billingTypes.filter(function (item) {
        return item.visible;
      });
    },
  },
  methods: {
    getBillingTypeTitle(item) {
      let locale = this.$store.state.locale.toLowerCase();
      let localeDefault = this.$config.i18nFallbackLocale.toLowerCase();
      if (item.hasOwnProperty("title_" + locale))
        return item["title_" + locale];
      else return item["title_" + localeDefault];
    },
    getBillingTypeNote(item) {
      let locale = this.$store.state.locale.toLowerCase();
      let localeDefault = this.$config.i18nFallbackLocale.toLowerCase();
      let note = "";
      if (item.hasOwnProperty("note_" + locale)) note = item["note_" + locale];
      else if (item.hasOwnProperty("note_" + localeDefault))
        note = item["note_" + localeDefault];
      else if (item.disabled) note = this.$i18n.t("contact support");
      if (note) return " (" + note + ")";
      return "";
    },
    createCustomer() {
      var that = this;
      this.loading = true;
      this.$http
        .post("customers", {
          address: this.address,
          billing_type: this.billingTypeValue,
          billing_period: this.billingPeriodValue,
        })
        .then((response) => {
          that.$emit("created", response.data);
          that.address = { ...emptyAddress };
        })
        .catch((error) => {
          that.$refs.obs.setErrors(error.data);
          that.showErrors(that.$refs.obs.getUnresolvedErrors(error));
        })
        .finally(function () {
          that.loading = false;
        });
    },
  },
  mounted() {},
};
</script>

<i18n>
{
  "en": {
    "contact support": "contact support",
    "create new customer": "Create new customer settings",
    "create":"Create",
    "cancel": "Cancel",
    "billing type": "Billing type",
    "billing period": "Billing period",
    "monthly in advance": "Monthly in advance",
    "quarterly in advance": "Quarterly in advance",
    "every six months in advance": "Every six months in advance",
    "annually in advance": "Annually in advance"
  },
  "de": {
    "contact support": "Support kontaktieren",
    "create new customer": "Neuen Kunden anlegen",
    "create":"Anlegen",
    "cancel": "Abbrechen",
    "billing type": "Rechnungsart",
    "billing period": "Verrechungsperiode",
    "monthly in advance": "Monatlich im voraus",
    "quarterly in advance": "Quartalsweise im voraus",
    "every six months in advance": "Halbjährlich im voraus",
    "annually in advance": "Jährlich im voraus"
  }
}
</i18n>
